import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Plus, Building2, LogOut, TrendingUp, AlertCircle, FolderOpen, FileText, Settings, Trash2, Menu, X, User, Download, ChevronRight, DollarSign, Edit2, BarChart3, Upload, FileSpreadsheet, ArrowRight, Check, Users, Calculator, HelpCircle, CheckCircle2, PlayCircle, Gavel, ShieldCheck, Briefcase, TrendingUp as InvestorIcon, Zap, CreditCard, LayoutGrid, Image, Table, Activity } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { apiService } from '@/lib/api';
import { WelcomeTour, useTour } from '@/components/WelcomeTour';
import { PERMISSIONS, hasPermission, ROLES } from '@/lib/permissions';
import UpgradeModal from '@/components/UpgradeModal';
import PaymentRequiredModal from '@/components/PaymentRequiredModal';
import { ControlTowerLayout, VisualSiteLayout, FinancialLedgerLayout, CommandCenterLayout } from '@/components/dashboard';
import SelectProjectForDrawDialog from '@/components/SelectProjectForDrawDialog';

function Dashboard({ user, onLogout }) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [projects, setProjects] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isTeamMember, setIsTeamMember] = useState(false);
  
  // Welcome Tour
  const { showTour, hasSeenTour, startTour, completeTour, closeTour } = useTour();
  
  // Role-based permissions state
  const [teamRoles, setTeamRoles] = useState([]); // Array of roles for team member
  const [assignedProjects, setAssignedProjects] = useState([]); // For project managers
  const [teamAccessInfo, setTeamAccessInfo] = useState(null); // Full team access info
  
  // Subscription/Project limit state
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  
  // Dashboard layout preference
  // Command Center is now the default landing dashboard (Feb 2026).
  // Other layouts (visual_site / control_tower / financial_ledger) remain functional
  // for legacy users whose stored preference still points to them, but the picker UI
  // has been retired. Anyone without a stored preference lands on Command Center.
  const getInitialLayout = () => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try {
        const parsed = JSON.parse(storedUser);
        if (parsed.dashboard_preference) return parsed.dashboard_preference;
      } catch (e) {
        // Ignore parse errors
      }
    }
    if (user?.dashboard_preference) return user.dashboard_preference;
    return 'command_center'; // Default (Feb 2026)
  };
  
  const [dashboardLayout, setDashboardLayout] = useState(getInitialLayout);
  const [showLayoutPicker, setShowLayoutPicker] = useState(false);
  
  // Project limits state
  const [projectLimits, setProjectLimits] = useState(null);
  
  // Project selection for draw request
  const [isSelectProjectDialogOpen, setIsSelectProjectDialogOpen] = useState(false);
  
  // Permission helper - checks if user can perform action
  const can = (permission) => {
    if (!isTeamMember) return true; // Owner has all permissions
    return hasPermission(teamRoles, permission);
  };
  
  const [newProject, setNewProject] = useState({
    name: '',
    address: '',
    property_type: 'residential',
    units: 1,
    sqft: 0,
    purchase_price_home: 0,
    purchase_price_land: 0,
    construction_budget: 0,
    contingency_budget: 0,
    insurance: 0,
    total_budget: 0,
    start_date: '',
    projected_completion_date: '',
  });
  const [buildingComponents, setBuildingComponents] = useState([
    { name: 'Main Dwelling', units: 0, sqft: 0 }
  ]);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  
  // Excel Import state
  const [isImportDialogOpen, setIsImportDialogOpen] = useState(false);
  const [importStep, setImportStep] = useState(1); // 1: upload, 2: mapping, 3: phase assignment
  const [importFile, setImportFile] = useState(null);
  const [importPreview, setImportPreview] = useState(null);
  const [importProjectName, setImportProjectName] = useState('');
  const [columnMapping, setColumnMapping] = useState({
    phase_name: '',
    category: '',
    estimated_cost: '',
    actual_cost: '',
    contractor: '',
    is_paid: '',
    phase_type: ''
  });
  const [isImporting, setIsImporting] = useState(false);
  const fileInputRef = useRef(null);
  
  // Phase assignment for imported items
  const [importedItems, setImportedItems] = useState([]);
  const CONSTRUCTION_PHASES = [
    { id: 1, name: 'Planning and Permitting', keywords: ['permit', 'plan', 'design', 'architect', 'survey', 'legal', 'appraisal', 'closing cost', 'lot purchase', 'purchase', 'title', 'engineer', 'soil report', 'zoning'] },
    { id: 2, name: 'Site Preparation and Foundation', keywords: ['site prep', 'excavat', 'foundation', 'concrete', 'footer', 'footing', 'grade', 'grading', 'dirt', 'erosion', 'backfill', 'demo', 'tree service', 'clear'] },
    { id: 3, name: 'Framing', keywords: ['frame', 'framing', 'lumber', 'truss', 'sheathing', 'stud', 'joist', 'rafter', 'beam', 'header'] },
    { id: 4, name: 'Exterior and Systems (Rough-in)', keywords: ['roof', 'window', 'siding', 'plumb', 'electric', 'hvac', 'mechanical', 'rough', 'duct', 'wire', 'pipe', 'turnkey', 'gutter', 'soffit', 'fascia', 'brick', 'exterior door'] },
    { id: 5, name: 'Insulation and Drywall', keywords: ['insul', 'drywall', 'sheetrock', 'tape', 'mud', 'texture'] },
    { id: 6, name: 'Interior Finishes', keywords: ['trim', 'cabinet', 'counter', 'floor', 'tile', 'paint', 'interior door', 'millwork', 'finish', 'baseboard', 'shelving', 'closet', 'carpentry', 'door hardware', 'vanity', 'mirror'] },
    { id: 7, name: 'Fixtures and Exterior Finishing', keywords: ['fixture', 'appliance', 'landscape', 'deck', 'patio', 'fence', 'driveway', 'garage door', 'light material', 'tub', 'shower', 'sink', 'toilet', 'faucet'] },
    { id: 8, name: 'Final Walkthrough and Handover', keywords: ['final clean', 'punch', 'cleaning', 'certificate', 'occupancy', 'touch up'] },
    { id: 0, name: 'Holding Costs', keywords: ['dumpster', 'utility', 'utilities', 'insurance', 'interest', 'loan', 'tax', 'taxes', 'rental', 'storage', 'port-a', 'porta potty', 'temporary', 'monthly', 'carrying cost', 'holding'] },
    { id: -1, name: 'Contingency', keywords: ['contingency', 'unexpected', 'reserve', 'buffer', 'cushion', 'overage'] }
  ];

  useEffect(() => {
    // Only load data when user is available
    if (user?.id) {
      loadProjects();
      checkTeamMembership();
      // Auto-upgrade legacy dashboard preferences to command_center (Feb 2026 — Iter 56).
      // Other layouts have been retired from the picker.
      const legacyLayouts = ['visual_site', 'control_tower', 'financial_ledger'];
      if (legacyLayouts.includes(dashboardLayout)) {
        setDashboardLayout('command_center');
        // Persist to backend (silent — no toast)
        apiService.updateDashboardPreference(user.id, 'command_center').catch(() => {});
        const storedUser = localStorage.getItem('user');
        if (storedUser) {
          try {
            const updatedUser = { ...JSON.parse(storedUser), dashboard_preference: 'command_center' };
            localStorage.setItem('user', JSON.stringify(updatedUser));
          } catch (e) { /* ignore */ }
        }
      }
    }
  }, [user?.id]); // Re-run when user.id changes

  // Show welcome tour for first-time users
  useEffect(() => {
    if (user?.id && !hasSeenTour && !isLoading) {
      // Small delay to let the page render first
      const timer = setTimeout(() => {
        startTour();
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [user?.id, hasSeenTour, isLoading]);

  // Deep-link support: the redesigned dashboard (/dashboard-v2-preview) routes
  // its "Start a project" / "Migrate existing project" CTAs here via ?new=1 /
  // ?import=1 so they reuse the existing, proven create + import flows.
  useEffect(() => {
    if (searchParams.get('new') === '1') {
      setIsDialogOpen(true);
      searchParams.delete('new');
      setSearchParams(searchParams, { replace: true });
    } else if (searchParams.get('import') === '1') {
      setIsImportDialogOpen(true);
      searchParams.delete('import');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const checkTeamMembership = async () => {
    try {
      const response = await apiService.getMyTeamAccess(user.id);
      // If user has any team access, they're viewing as a team member
      if (response.data && response.data.length > 0) {
        setIsTeamMember(true);
        const access = response.data[0];
        setTeamAccessInfo(access);
        
        // Get roles - support both new array format and legacy single role
        const roles = access.roles && access.roles.length > 0 
          ? access.roles 
          : [access.role || 'accountant'];
        setTeamRoles(roles);
        
        // Get assigned projects for project managers
        if (roles.includes('project_manager') && access.assigned_projects) {
          setAssignedProjects(access.assigned_projects);
        }
      }
    } catch (error) {
      console.error('Failed to check team membership:', error);
    }
  };

  const loadProjects = async () => {
    try {
      const response = await apiService.getProjects(user.id);
      setProjects(response.data);
      
      // Also fetch project limits
      try {
        const limitsResponse = await apiService.getProjectLimits(user.id);
        setProjectLimits(limitsResponse.data);
      } catch (limitsError) {
        console.error('Failed to load project limits:', limitsError);
      }
    } catch (error) {
      console.error('Failed to load projects:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLayoutChange = async (newLayout) => {
    try {
      await apiService.updateDashboardPreference(user.id, newLayout);
      setDashboardLayout(newLayout);
      setShowLayoutPicker(false);
      
      // Update localStorage to persist the preference
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        const updatedUser = { ...JSON.parse(storedUser), dashboard_preference: newLayout };
        localStorage.setItem('user', JSON.stringify(updatedUser));
      }
      
      toast.success('Dashboard layout updated');
    } catch (error) {
      console.error('Failed to update dashboard preference:', error);
      toast.error('Failed to update layout preference');
    }
  };

  const handleDeleteProject = async (projectId, e) => {
    e.stopPropagation(); // Prevent card click navigation
    
    if (!window.confirm('Are you sure you want to delete this project? This action cannot be undone and will delete all project data including phases, documents, and payments.')) {
      return;
    }
    
    try {
      await apiService.deleteProject(projectId);
      toast.success('Project deleted successfully');
      loadProjects();
    } catch (error) {
      toast.error('Failed to delete project');
      console.error(error);
    }
  };

  const handleEditProject = (project, e) => {
    e.stopPropagation(); // Prevent card click navigation
    setEditingProject({
      ...project,
      start_date: project.start_date ? project.start_date.split('T')[0] : '',
      projected_completion_date: project.projected_completion_date ? project.projected_completion_date.split('T')[0] : '',
    });
    setIsEditDialogOpen(true);
  };

  const handleUpdateProject = async (e) => {
    e.preventDefault();
    
    try {
      const updateData = {
        name: editingProject.name,
        address: editingProject.address,
        property_type: editingProject.property_type,
        units: parseInt(editingProject.units || 1),
        sqft: parseFloat(editingProject.sqft || 0),
        purchase_price_home: parseFloat(editingProject.purchase_price_home || 0),
        purchase_price_land: parseFloat(editingProject.purchase_price_land || 0),
        construction_budget: parseFloat(editingProject.construction_budget || 0),
        contingency_budget: parseFloat(editingProject.contingency_budget || 0),
        insurance: parseFloat(editingProject.insurance || 0),
        start_date: editingProject.start_date ? new Date(editingProject.start_date).toISOString() : null,
        projected_completion_date: editingProject.projected_completion_date ? new Date(editingProject.projected_completion_date).toISOString() : null,
        legal_description: editingProject.legal_description || null,
        return_address: editingProject.return_address || null,
        // Loan term + listing info (Feb 2026)
        loan_period_months: editingProject.loan_period_months ? parseInt(editingProject.loan_period_months) : null,
        loan_term_extended: !!editingProject.loan_term_extended,
        loan_extended_to_months: editingProject.loan_term_extended && editingProject.loan_extended_to_months
          ? parseInt(editingProject.loan_extended_to_months) : null,
        listing_type: editingProject.listing_type || null,
        brokerage_name: editingProject.listing_type === 'brokerage' ? (editingProject.brokerage_name || null) : null,
        agent_name: editingProject.listing_type === 'brokerage' ? (editingProject.agent_name || null) : null,
      };
      
      // Recalculate total budget
      updateData.total_budget = updateData.purchase_price_home + updateData.purchase_price_land + 
                                 updateData.construction_budget + updateData.contingency_budget + updateData.insurance;

      await apiService.updateProject(editingProject.id, updateData);
      // Save typed brokerage to history for autocomplete
      if (updateData.brokerage_name) {
        try {
          const { saveBrokerageToHistory } = await import('@/lib/brokerages');
          saveBrokerageToHistory(updateData.brokerage_name);
        } catch (e) { /* non-fatal */ }
      }
      toast.success('Project updated successfully!');
      setIsEditDialogOpen(false);
      setEditingProject(null);
      loadProjects();
    } catch (error) {
      toast.error('Failed to update project');
      console.error(error);
    }
  };

  // Excel Import handlers
  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      toast.error('Please select an Excel file (.xlsx or .xls)');
      return;
    }
    
    setImportFile(file);
    setImportProjectName(file.name.replace(/\.(xlsx|xls)$/, ''));
    
    try {
      const response = await apiService.previewExcelImport(file);
      setImportPreview(response.data);
      setImportStep(2);
      
      // Smart auto-detect column mappings based on common names
      const columns = response.data.columns;
      const newMapping = { ...columnMapping };
      
      // Priority-based matching for more accurate detection
      const mappingRules = [
        { field: 'phase_name', keywords: ['line item', 'item', 'description', 'name', 'phase', 'task', 'work'], priority: ['line item', 'item', 'description'] },
        { field: 'estimated_cost', keywords: ['cost', 'budget', 'estimate', 'amount', 'price', 'total'], priority: ['cost', 'budget', 'estimate'] },
        { field: 'actual_cost', keywords: ['actual', 'spent', 'paid amount', 'real'], priority: ['actual', 'spent'] },
        { field: 'contractor', keywords: ['contractor', 'vendor', 'supplier', 'company', 'sub'], priority: ['contractor', 'vendor'] },
        { field: 'category', keywords: ['category', 'type', 'class', 'group'], priority: ['category', 'type'] },
        { field: 'is_paid', keywords: ['paid', 'status', 'complete'], priority: ['paid', 'status'] }
      ];
      
      mappingRules.forEach(rule => {
        // First try priority keywords (exact matches)
        for (const keyword of rule.priority) {
          const match = columns.find(col => col.toLowerCase() === keyword);
          if (match) {
            newMapping[rule.field] = match;
            return;
          }
        }
        // Then try partial matches
        for (const keyword of rule.keywords) {
          const match = columns.find(col => col.toLowerCase().includes(keyword));
          if (match && !newMapping[rule.field]) {
            newMapping[rule.field] = match;
            break;
          }
        }
      });
      
      setColumnMapping(newMapping);
    } catch (error) {
      toast.error('Failed to read Excel file');
      console.error(error);
    }
  };
  
  // Auto-assign phase based on item name keywords
  // Uses longest match first to avoid partial matches (e.g., "landscape" vs "land")
  const autoAssignPhase = (itemName) => {
    const nameLower = itemName.toLowerCase();
    
    let bestMatch = { phaseId: 1, matchLength: 0 };
    
    for (const phase of CONSTRUCTION_PHASES) {
      for (const keyword of phase.keywords) {
        if (nameLower.includes(keyword)) {
          // Prefer longer keyword matches (more specific)
          if (keyword.length > bestMatch.matchLength) {
            bestMatch = { phaseId: phase.id, matchLength: keyword.length };
          }
        }
      }
    }
    
    return bestMatch.phaseId;
  };
  
  // Process imported items and prepare for phase assignment
  const processImportedItems = () => {
    if (!importPreview || !columnMapping.phase_name) return;
    
    const nameCol = columnMapping.phase_name;
    const costCol = columnMapping.estimated_cost;
    const actualCol = columnMapping.actual_cost;
    
    const colIndex = {
      name: importPreview.columns.indexOf(nameCol),
      cost: costCol ? importPreview.columns.indexOf(costCol) : -1,
      actual: actualCol ? importPreview.columns.indexOf(actualCol) : -1
    };
    
    // Use ALL rows from the backend, not just preview rows
    const dataRows = importPreview.all_rows || importPreview.preview_rows;
    
    // Summary row keywords to filter out - these are totals, not actual items
    const summaryRowKeywords = ['total', 'subtotal', 'sub-total', 'grand total', 'sum', 'totals'];
    
    const items = dataRows
      .filter(row => {
        const name = row[colIndex.name]?.trim() || '';
        if (!name) return false;
        
        // Filter out summary/total rows
        const nameLower = name.toLowerCase();
        const isSummaryRow = summaryRowKeywords.some(keyword => 
          nameLower === keyword || 
          nameLower.startsWith(keyword + ' ') || 
          nameLower.startsWith(keyword + ':')
        );
        
        return !isSummaryRow;
      })
      .map((row, idx) => {
        const name = row[colIndex.name]?.trim() || '';
        const cost = colIndex.cost >= 0 ? parseFloat(row[colIndex.cost]?.replace(/[$,]/g, '') || '0') : 0;
        const actual = colIndex.actual >= 0 ? parseFloat(row[colIndex.actual]?.replace(/[$,]/g, '') || '0') : 0;
        
        return {
          id: idx,
          name,
          estimatedCost: isNaN(cost) ? 0 : cost,
          actualCost: isNaN(actual) ? 0 : actual,
          assignedPhase: autoAssignPhase(name)
        };
      });
    
    setImportedItems(items);
    setImportStep(3);
  };
  
  // Update phase assignment for an item
  const updateItemPhase = (itemId, phaseId) => {
    setImportedItems(prev => prev.map(item => 
      item.id === itemId ? { ...item, assignedPhase: phaseId } : item
    ));
  };
  
  // Bulk assign all items to a phase
  const bulkAssignPhase = (phaseId) => {
    setImportedItems(prev => prev.map(item => ({ ...item, assignedPhase: phaseId })));
  };

  const handleImportProject = async () => {
    if (!importProjectName.trim()) {
      toast.error('Please enter a project name');
      return;
    }
    
    if (importedItems.length === 0) {
      toast.error('No items to import');
      return;
    }
    
    setIsImporting(true);
    try {
      // Create new import payload with phase assignments
      const importData = {
        project_name: importProjectName.trim(),
        items: importedItems.map(item => ({
          name: item.name,
          estimated_cost: item.estimatedCost,
          actual_cost: item.actualCost,
          construction_phase: item.assignedPhase
        }))
      };
      
      const response = await apiService.importProjectWithPhases(user.id, importData);
      
      const projectId = response.data.project_id;
      const itemsCreated = response.data.items_created || 0;
      const phasesUsed = response.data.phases_used || 0;
      
      toast.success(`Imported ${itemsCreated} items into ${phasesUsed} phases!`);
      
      // Close dialog and reset state
      setIsImportDialogOpen(false);
      resetImportState();
      
      // Reload projects list
      await loadProjects();
      
      // Navigate to the new project after a short delay to ensure state is clean
      if (projectId) {
        setTimeout(() => {
          navigate(`/projects/${projectId}`);
        }, 500);
      }
    } catch (error) {
      console.error('Import error:', error);
      toast.error(error.response?.data?.detail || 'Failed to import project');
    } finally {
      setIsImporting(false);
    }
  };

  const resetImportState = () => {
    setImportStep(1);
    setImportFile(null);
    setImportPreview(null);
    setImportProjectName('');
    setImportedItems([]);
    setColumnMapping({
      phase_name: '',
      category: '',
      estimated_cost: '',
      actual_cost: '',
      contractor: '',
      is_paid: '',
      phase_type: ''
    });
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Check if user can create a new project
  const handleNewProjectClick = async () => {
    try {
      const { data } = await apiService.canCreateProject(user.id);
      
      if (data.reason === 'card_required') {
        // User needs to add payment method first
        setShowPaymentModal(true);
        return;
      }
      
      if (!data.can_create) {
        // User has reached project limit
        setSubscriptionStatus(data);
        setShowUpgradeModal(true);
        return;
      }
      
      // User can create project — launch the import-first new-project flow
      navigate('/new-project-vision');
    } catch (error) {
      console.error('Failed to check project limit:', error);
      // Fallback: still send them to the new-project flow
      navigate('/new-project-vision');
    }
  };

  const handlePaymentSuccess = () => {
    setShowPaymentModal(false);
    toast.success('Payment method added! Starting your 30-day free trial.');
    // Now allow creating project
    navigate('/new-project-vision');
  };

  const handleUpgradeSelect = (planId) => {
    navigate(`/pricing?plan=${planId}`);
  };

  const handleCreateProject = async (e) => {
    e.preventDefault();
    
    try {
      // Calculate total units and sqft from property categories
      const totalUnits = buildingComponents.reduce((sum, c) => sum + parseInt(c.units || 0), 0);
      const totalSqft = buildingComponents.reduce((sum, c) => sum + parseFloat(c.sqft || 0), 0);
      
      // Calculate total budget from all budget fields
      const purchasePriceHome = parseFloat(newProject.purchase_price_home || 0);
      const purchasePriceLand = parseFloat(newProject.purchase_price_land || 0);
      const constructionBudget = parseFloat(newProject.construction_budget || 0);
      const contingencyBudget = parseFloat(newProject.contingency_budget || 0);
      const insurance = parseFloat(newProject.insurance || 0);
      const totalBudget = purchasePriceHome + purchasePriceLand + constructionBudget + contingencyBudget + insurance;
      
      const projectData = {
        ...newProject,
        user_id: user.id,
        units: totalUnits || parseInt(newProject.units),
        sqft: totalSqft || parseFloat(newProject.sqft),
        purchase_price_home: purchasePriceHome,
        purchase_price_land: purchasePriceLand,
        construction_budget: constructionBudget,
        contingency_budget: contingencyBudget,
        insurance: insurance,
        total_budget: totalBudget,
        start_date: newProject.start_date ? new Date(newProject.start_date).toISOString() : null,
        projected_completion_date: newProject.projected_completion_date ? new Date(newProject.projected_completion_date).toISOString() : null,
      };

      const projectResponse = await apiService.createProject(projectData);
      const projectId = projectResponse.data.id;
      
      // Create property categories
      for (const component of buildingComponents) {
        if (component.name && (component.units > 0 || component.sqft > 0)) {
          await apiService.createBuildingComponent(projectId, {
            name: component.name,
            units: parseInt(component.units || 0),
            sqft: parseFloat(component.sqft || 0),
          });
        }
      }
      
      toast.success('Project created successfully!');
      setIsDialogOpen(false);
      setNewProject({
        name: '',
        address: '',
        property_type: 'residential',
        units: 1,
        sqft: 0,
        purchase_price_home: 0,
        purchase_price_land: 0,
        construction_budget: 0,
        contingency_budget: 0,
        insurance: 0,
        total_budget: 0,
        start_date: '',
        projected_completion_date: '',
      });
      setBuildingComponents([{ name: 'Main Dwelling', units: 0, sqft: 0 }]);
      loadProjects();
      // v2.6.6 — the classic dashboard is now only reached via the new
      // dashboard's "create project" deep-link, so return the user to the
      // redesigned /dashboard once their project exists.
      navigate('/dashboard');
    } catch (error) {
      // Check if it's a project limit error (403)
      if (error.response?.status === 403) {
        toast.error(error.response.data?.detail || 'You have reached your project limit. Please upgrade your plan.');
      } else {
        toast.error('Failed to create project');
      }
      console.error(error);
    }
  };

  const getProjectDisplayStatus = (project) => {
    if (project.status === 'completed') {
      return { label: 'Completed', color: 'bg-green-100 text-green-700', showIcon: true };
    }
    
    // Check if project has a future start date
    if (project.start_date) {
      const startDate = new Date(project.start_date);
      const today = new Date();
      today.setHours(0, 0, 0, 0); // Reset to start of day for comparison
      
      if (startDate > today) {
        return { label: 'Scheduled', color: 'bg-orange-100 text-orange-700', showIcon: false };
      }
    }
    
    if (project.status === 'active') {
      return { label: 'Active', color: 'bg-blue-600 text-white', showIcon: false };
    }
    
    return { label: project.status.charAt(0).toUpperCase() + project.status.slice(1), color: 'bg-slate-100 text-slate-700', showIcon: false };
  };

  const getProjectStats = () => {
    const activeProjects = projects.filter(p => {
      if (p.status !== 'active') return false;
      // Don't count scheduled projects as active
      if (p.start_date) {
        const startDate = new Date(p.start_date);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        if (startDate > today) return false;
      }
      return true;
    }).length;
    const scheduledProjects = projects.filter(p => {
      if (p.start_date) {
        const startDate = new Date(p.start_date);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return startDate > today;
      }
      return false;
    }).length;
    const inProgressProjects = projects.filter(p => p.status === 'active').length - scheduledProjects;
    const totalBudget = projects.reduce((sum, p) => sum + p.total_budget, 0);
    const totalFundsAvailable = projects.reduce((sum, p) => sum + (p.loan_available_for_draw || 0), 0);
    return { activeProjects, totalBudget, totalFundsAvailable, inProgressProjects, scheduledProjects };
  };

  const stats = getProjectStats();
  
  // Get role display info for banner
  const getRoleBanner = () => {
    if (!isTeamMember || teamRoles.length === 0) return null;
    
    const roleNames = teamRoles.map(r => {
      const info = ROLES[r];
      return info ? info.name : r.replace('_', ' ');
    }).join(' & ');
    
    // Role-specific message
    if (teamRoles.includes('project_manager')) {
      return {
        message: `You are viewing as ${roleNames} — You can manage your assigned projects`,
        icon: Briefcase,
        bgColor: 'bg-blue-100 border-blue-300 text-blue-800'
      };
    } else if (teamRoles.includes('accountant')) {
      return {
        message: `You are viewing as ${roleNames} — You have read-only access to financials and reports`,
        icon: Calculator,
        bgColor: 'bg-emerald-100 border-emerald-300 text-emerald-800'
      };
    } else if (teamRoles.includes('investor')) {
      return {
        message: `You are viewing as ${roleNames} — You have read-only access to project progress`,
        icon: InvestorIcon,
        bgColor: 'bg-amber-100 border-amber-300 text-amber-800'
      };
    }
    
    return {
      message: `You are viewing as ${roleNames}`,
      icon: AlertCircle,
      bgColor: 'bg-slate-100 border-slate-300 text-slate-800'
    };
  };
  
  const roleBanner = getRoleBanner();

  // Handle Request Draw - open project selection dialog
  const handleRequestDraw = () => {
    const projectsWithFunds = projects.filter(p => (p.loan_available_for_draw || 0) > 0);
    if (projectsWithFunds.length === 0) {
      toast.error('No projects have funds available for draw');
    } else if (projectsWithFunds.length === 1) {
      // Only one project with funds - go directly to it
      navigate(`/project/${projectsWithFunds[0].id}?openDraw=true`);
    } else {
      // Multiple projects - show selection dialog
      setIsSelectProjectDialogOpen(true);
    }
  };

  // Handle project cover image update
  const handleProjectUpdate = (projectId, updates) => {
    setProjects(prev => prev.map(p => 
      p.id === projectId ? { ...p, ...updates } : p
    ));
  };

  // Layout-specific props
  const layoutProps = {
    projects,
    totalFundsAvailable: stats.totalFundsAvailable,
    activeCount: stats.activeProjects,
    scheduledCount: stats.scheduledProjects,
    totalBudget: stats.totalBudget,
    onNewProject: handleNewProjectClick,
    onImportSpreadsheet: () => setIsImportDialogOpen(true),
    onRequestDraw: handleRequestDraw,
    onDeleteProject: handleDeleteProject,
    onEditProject: handleEditProject,
    canCreateProject: can(PERMISSIONS.CREATE_PROJECTS),
    isTeamMember,
    onProjectUpdate: handleProjectUpdate,
    userId: user?.id,
  };

  // Render dashboard layout - Command Center is now the only dashboard (Feb 2026).
  // Legacy layouts (visual_site/control_tower/financial_ledger) have been retired
  // from the picker; existing users with those stored preferences are auto-upgraded.
  const renderDashboardLayout = () => {
    return <CommandCenterLayout {...layoutProps} />;
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Team Member Banner */}
      {isTeamMember && roleBanner && (
        <div className={`${roleBanner.bgColor} border-b py-2 px-4`}>
          <div className="max-w-7xl mx-auto flex items-center justify-center gap-2 text-sm">
            <roleBanner.icon className="w-4 h-4" />
            <span>{roleBanner.message}</span>
          </div>
        </div>
      )}
      
      {/* Main Content */}
      <main className="max-w-7xl mx-auto">
        {/* Render the selected dashboard layout */}
        {renderDashboardLayout()}
      </main>

      {/* Import Spreadsheet Dialog */}
      <Dialog open={isImportDialogOpen} onOpenChange={(open) => {
        setIsImportDialogOpen(open);
        if (!open) resetImportState();
      }}>
                <DialogContent className="sm:max-w-[700px] max-h-[90vh] flex flex-col">
                  <DialogHeader className="flex-shrink-0">
                  <DialogTitle className="text-2xl font-bold flex items-center gap-2">
                    <FileSpreadsheet className="w-6 h-6 text-green-600" />
                    Import Project from Excel
                  </DialogTitle>
                  <DialogDescription>
                    Upload a spreadsheet to create a new project with phases
                  </DialogDescription>
                </DialogHeader>

                {/* Step Indicator */}
                <div className="flex items-center justify-center gap-2 py-4 border-b">
                  <div className={`flex items-center gap-2 ${importStep >= 1 ? 'text-blue-600' : 'text-slate-400'}`}>
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${importStep >= 1 ? 'bg-blue-600 text-white' : 'bg-slate-200'}`}>
                      {importStep > 1 ? <Check className="w-4 h-4" /> : '1'}
                    </div>
                    <span className="text-sm font-medium">Upload</span>
                  </div>
                  <ArrowRight className="w-4 h-4 text-slate-300" />
                  <div className={`flex items-center gap-2 ${importStep >= 2 ? 'text-blue-600' : 'text-slate-400'}`}>
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${importStep >= 2 ? 'bg-blue-600 text-white' : 'bg-slate-200'}`}>
                      {importStep > 2 ? <Check className="w-4 h-4" /> : '2'}
                    </div>
                    <span className="text-sm font-medium">Map Columns</span>
                  </div>
                  <ArrowRight className="w-4 h-4 text-slate-300" />
                  <div className={`flex items-center gap-2 ${importStep >= 3 ? 'text-blue-600' : 'text-slate-400'}`}>
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${importStep >= 3 ? 'bg-blue-600 text-white' : 'bg-slate-200'}`}>
                      3
                    </div>
                    <span className="text-sm font-medium">Assign Phases</span>
                  </div>
                </div>

                <div className="flex-1 overflow-y-auto py-4">
                  {/* Step 1: Upload */}
                  {importStep === 1 && (
                    <div className="space-y-4">
                      <div 
                        className="border-2 border-dashed border-slate-300 rounded-lg p-8 text-center hover:border-blue-400 transition-colors cursor-pointer"
                        onClick={() => fileInputRef.current?.click()}
                      >
                        <Upload className="w-12 h-12 mx-auto text-slate-400 mb-4" />
                        <p className="text-lg font-medium text-slate-700">
                          Click to upload or drag and drop
                        </p>
                        <p className="text-sm text-slate-500 mt-1">
                          Excel files (.xlsx, .xls)
                        </p>
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept=".xlsx,.xls"
                          className="hidden"
                          onChange={handleFileSelect}
                        />
                      </div>
                      <div className="bg-blue-50 p-4 rounded-lg">
                        <p className="text-sm text-blue-800 font-medium mb-2">Tips for best results:</p>
                        <ul className="text-sm text-blue-700 list-disc list-inside space-y-1">
                          <li>Include column headers in the first row</li>
                          <li>One row per phase/line item</li>
                          <li>Include columns for: Name, Category, Estimated Cost</li>
                          <li>Optional: Contractor, Paid Status, Actual Cost</li>
                        </ul>
                      </div>
                    </div>
                  )}

                  {/* Step 2: Column Mapping */}
                  {importStep === 2 && importPreview && (
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="import-project-name">Project Name</Label>
                        <Input
                          id="import-project-name"
                          value={importProjectName}
                          onChange={(e) => setImportProjectName(e.target.value)}
                          placeholder="Enter project name"
                          className="h-11"
                        />
                      </div>

                      <div className="border rounded-lg p-4 space-y-3">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium text-slate-700">Map your columns:</p>
                            <p className="text-sm text-slate-500">We auto-detected your columns. Adjust if needed.</p>
                          </div>
                          {columnMapping.phase_name && (
                            <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">
                              ✓ Auto-mapped
                            </span>
                          )}
                        </div>
                        
                        <div className="grid grid-cols-2 gap-3">
                          <div className="space-y-1">
                            <Label className="text-sm">Item Name *</Label>
                            <Select value={columnMapping.phase_name} onValueChange={(v) => setColumnMapping({...columnMapping, phase_name: v})}>
                              <SelectTrigger className="h-10">
                                <SelectValue placeholder="Select column" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="__none__">-- Not mapped --</SelectItem>
                                {importPreview.columns.map(col => (
                                  <SelectItem key={col} value={col}>{col}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-1">
                            <Label className="text-sm">Estimated Cost</Label>
                            <Select value={columnMapping.estimated_cost} onValueChange={(v) => setColumnMapping({...columnMapping, estimated_cost: v})}>
                              <SelectTrigger className="h-10">
                                <SelectValue placeholder="Select column" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="__none__">-- Not mapped --</SelectItem>
                                {importPreview.columns.map(col => (
                                  <SelectItem key={col} value={col}>{col}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-1">
                            <Label className="text-sm">Actual Cost</Label>
                            <Select value={columnMapping.actual_cost} onValueChange={(v) => setColumnMapping({...columnMapping, actual_cost: v})}>
                              <SelectTrigger className="h-10">
                                <SelectValue placeholder="Select column" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="__none__">-- Not mapped --</SelectItem>
                                {importPreview.columns.map(col => (
                                  <SelectItem key={col} value={col}>{col}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                      </div>

                      {/* Preview Table */}
                      <div className="border rounded-lg overflow-hidden">
                        <p className="font-medium text-slate-700 px-4 py-2 bg-slate-50 border-b">
                          Preview ({importPreview.total_rows} rows total)
                        </p>
                        <div className="overflow-x-auto max-h-48">
                          <table className="w-full text-sm">
                            <thead className="bg-slate-100 sticky top-0">
                              <tr>
                                {importPreview.columns.map(col => (
                                  <th key={col} className="px-3 py-2 text-left font-medium text-slate-600 whitespace-nowrap">
                                    {col}
                                  </th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {importPreview.preview_rows.map((row, idx) => (
                                <tr key={idx} className="border-t">
                                  {row.map((cell, cellIdx) => (
                                    <td key={cellIdx} className="px-3 py-2 text-slate-700 whitespace-nowrap">
                                      {cell}
                                    </td>
                                  ))}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Step 3: Phase Assignment */}
                  {importStep === 3 && importedItems.length > 0 && (
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-slate-700">Assign items to phases</p>
                          <p className="text-sm text-slate-500">We auto-assigned based on item names. Adjust as needed.</p>
                        </div>
                        <Select onValueChange={(v) => bulkAssignPhase(parseInt(v))}>
                          <SelectTrigger className="w-48 h-9 text-sm">
                            <SelectValue placeholder="Assign all to..." />
                          </SelectTrigger>
                          <SelectContent>
                            {CONSTRUCTION_PHASES.filter(p => p.id > 0).map(phase => (
                              <SelectItem key={phase.id} value={phase.id.toString()}>
                                Phase {phase.id}: {phase.name.split(' ')[0]}
                              </SelectItem>
                            ))}
                            <SelectItem value="0" className="text-orange-600">
                              Holding Costs
                            </SelectItem>
                            <SelectItem value="-1" className="text-purple-600">
                              Contingency
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="border rounded-lg overflow-hidden max-h-96 overflow-y-auto">
                        <table className="w-full text-sm">
                          <thead className="bg-slate-100 sticky top-0">
                            <tr>
                              <th className="px-3 py-2 text-left font-medium text-slate-600">Item</th>
                              <th className="px-3 py-2 text-right font-medium text-slate-600 w-24">Est. Cost</th>
                              <th className="px-3 py-2 text-right font-medium text-slate-600 w-24">Actual</th>
                              <th className="px-3 py-2 text-left font-medium text-slate-600 w-56">Assign to Phase</th>
                            </tr>
                          </thead>
                          <tbody>
                            {importedItems.map((item) => (
                              <tr key={item.id} className="border-t hover:bg-slate-50">
                                <td className="px-3 py-2 text-slate-700">{item.name}</td>
                                <td className="px-3 py-2 text-right text-slate-600">
                                  {item.estimatedCost > 0 ? `$${item.estimatedCost.toLocaleString()}` : '-'}
                                </td>
                                <td className="px-3 py-2 text-right text-slate-600">
                                  {item.actualCost > 0 ? `$${item.actualCost.toLocaleString()}` : '-'}
                                </td>
                                <td className="px-3 py-1">
                                  <Select 
                                    value={item.assignedPhase.toString()} 
                                    onValueChange={(v) => updateItemPhase(item.id, parseInt(v))}
                                  >
                                    <SelectTrigger className={`h-8 text-xs ${item.assignedPhase === 0 ? 'border-orange-300 bg-orange-50' : item.assignedPhase === -1 ? 'border-purple-300 bg-purple-50' : ''}`}>
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {CONSTRUCTION_PHASES.filter(p => p.id > 0).map(phase => (
                                        <SelectItem key={phase.id} value={phase.id.toString()}>
                                          {phase.id}. {phase.name}
                                        </SelectItem>
                                      ))}
                                      <SelectItem value="0" className="text-orange-600 font-medium">
                                        ⚡ Holding Costs
                                      </SelectItem>
                                      <SelectItem value="-1" className="text-purple-600 font-medium">
                                        🛡️ Contingency
                                      </SelectItem>
                                    </SelectContent>
                                  </Select>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>

                      <div className="bg-blue-50 rounded-lg p-3 flex items-start gap-2">
                        <div className="text-blue-600 mt-0.5">💡</div>
                        <div className="text-sm text-blue-800">
                          <strong>Tip:</strong> Items will be added as materials in their assigned phase. 
                          You can change phases and add labor/inspections after import.
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="flex justify-between pt-4 border-t">
                  {importStep === 1 ? (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setIsImportDialogOpen(false)}
                    >
                      Cancel
                    </Button>
                  ) : (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        if (importStep === 3) {
                          setImportStep(2);
                          setImportedItems([]);
                        } else {
                          setImportStep(1);
                          setImportFile(null);
                          setImportPreview(null);
                        }
                      }}
                    >
                      Back
                    </Button>
                  )}
                  
                  {importStep === 2 && (
                    <Button
                      type="button"
                      className="bg-blue-600 hover:bg-blue-700"
                      onClick={processImportedItems}
                      disabled={!columnMapping.phase_name || columnMapping.phase_name === '__none__'}
                    >
                      Next: Assign Phases →
                    </Button>
                  )}
                  
                  {importStep === 3 && (
                    <Button
                      type="button"
                      className="bg-blue-600 hover:bg-blue-700"
                      onClick={handleImportProject}
                      disabled={isImporting || importedItems.length === 0}
                    >
                      {isImporting ? 'Importing...' : `Import ${importedItems.length} Items`}
                    </Button>
                  )}
                </div>
              </DialogContent>
            </Dialog>

      {/* New Project Dialog - Moved outside of conditional rendering */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
              <DialogContent className="sm:max-w-[600px] max-h-[90vh] flex flex-col">
                <DialogHeader className="flex-shrink-0">
                  <DialogTitle className="text-2xl font-bold">Create New Project</DialogTitle>
                  <DialogDescription>
                    Add a new construction project to track costs and progress
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleCreateProject} className="flex flex-col flex-1 min-h-0">
                  <div className="flex-1 overflow-y-auto space-y-4 pr-2">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2 space-y-2">
                      <Label htmlFor="project-name">Project Name</Label>
                      <Input
                      id="project-name"
                      data-testid="project-name-input"
                      placeholder="38th Street Build"
                      value={newProject.name}
                      onChange={(e) => setNewProject({ ...newProject, name: e.target.value })}
                      required
                      className="h-12"
                    />
                  </div>
                  <div className="col-span-2 space-y-2">
                    <Label htmlFor="address">Address</Label>
                    <Input
                      id="address"
                      data-testid="project-address-input"
                      placeholder="123 Main St, City, State"
                      value={newProject.address}
                      onChange={(e) => setNewProject({ ...newProject, address: e.target.value })}
                      required
                      className="h-12"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="property-type">Property Type</Label>
                    <Select
                      value={newProject.property_type}
                      onValueChange={(value) => setNewProject({ ...newProject, property_type: value })}
                    >
                      <SelectTrigger id="property-type" data-testid="property-type-select" className="h-12">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="residential">Residential</SelectItem>
                        <SelectItem value="commercial">Commercial</SelectItem>
                        <SelectItem value="mixed-use">Mixed-Use</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Budget Breakdown Section */}
                <div className="space-y-3 border-t pt-4">
                  <Label className="text-base font-semibold">Budget Breakdown</Label>
                  <p className="text-sm text-gray-500">
                    Separate costs for better tracking and future analysis
                  </p>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="purchase-home">Purchase Price - Home ($)</Label>
                      <Input
                        id="purchase-home"
                        type="number"
                        min="0"
                        step="0.01"
                        placeholder="0"
                        value={newProject.purchase_price_home}
                        onChange={(e) => setNewProject({ ...newProject, purchase_price_home: e.target.value })}
                        className="h-10"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="purchase-land">Purchase Price - Land ($)</Label>
                      <Input
                        id="purchase-land"
                        type="number"
                        min="0"
                        step="0.01"
                        placeholder="0"
                        value={newProject.purchase_price_land}
                        onChange={(e) => setNewProject({ ...newProject, purchase_price_land: e.target.value })}
                        className="h-10"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="construction-budget">Construction Budget ($)</Label>
                      <Input
                        id="construction-budget"
                        type="number"
                        min="0"
                        step="0.01"
                        placeholder="0"
                        value={newProject.construction_budget}
                        onChange={(e) => setNewProject({ ...newProject, construction_budget: e.target.value })}
                        className="h-10"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="contingency-budget">Contingency Budget ($)</Label>
                      <Input
                        id="contingency-budget"
                        type="number"
                        min="0"
                        step="0.01"
                        placeholder="0"
                        value={newProject.contingency_budget}
                        onChange={(e) => setNewProject({ ...newProject, contingency_budget: e.target.value })}
                        className="h-10"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="insurance">Insurance ($)</Label>
                      <Input
                        id="insurance"
                        type="number"
                        min="0"
                        step="0.01"
                        placeholder="0"
                        value={newProject.insurance}
                        onChange={(e) => setNewProject({ ...newProject, insurance: e.target.value })}
                        className="h-10"
                      />
                    </div>
                  </div>
                  {/* Total Display */}
                  <div className="flex justify-end pt-2 border-t">
                    <span className="text-lg font-semibold">
                      Total Budget: ${(
                        parseFloat(newProject.purchase_price_home || 0) +
                        parseFloat(newProject.purchase_price_land || 0) +
                        parseFloat(newProject.construction_budget || 0) +
                        parseFloat(newProject.contingency_budget || 0) +
                        parseFloat(newProject.insurance || 0)
                      ).toLocaleString()}
                    </span>
                  </div>
                </div>

                {/* Property Categories Section */}
                <div className="space-y-3 border-t pt-4">
                  <div className="flex items-center justify-between">
                    <Label className="text-base font-semibold">Property Categories</Label>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setBuildingComponents([...buildingComponents, { name: '', units: 0, sqft: 0 }])}
                      className="h-8"
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Add Category
                    </Button>
                  </div>
                  <p className="text-sm text-gray-500">
                    Define property categories (e.g., SF - Single Family, MF - Multifamily, Garage, ADU)
                  </p>
                  
                  {buildingComponents.map((component, index) => (
                    <div key={index} className="grid grid-cols-12 gap-2 items-end p-3 bg-gray-50 rounded-lg">
                      <div className="col-span-5">
                        <Label className="text-xs">Category Name</Label>
                        <Input
                          placeholder="e.g., SF - Single Family"
                          value={component.name}
                          onChange={(e) => {
                            const updated = [...buildingComponents];
                            updated[index].name = e.target.value;
                            setBuildingComponents(updated);
                          }}
                          className="h-10"
                        />
                      </div>
                      <div className="col-span-3">
                        <Label className="text-xs">Units</Label>
                        <Input
                          type="number"
                          min="0"
                          placeholder="0"
                          value={component.units}
                          onChange={(e) => {
                            const updated = [...buildingComponents];
                            updated[index].units = e.target.value;
                            setBuildingComponents(updated);
                          }}
                          className="h-10"
                        />
                      </div>
                      <div className="col-span-3">
                        <Label className="text-xs">Sq Ft</Label>
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          placeholder="0"
                          value={component.sqft}
                          onChange={(e) => {
                            const updated = [...buildingComponents];
                            updated[index].sqft = e.target.value;
                            setBuildingComponents(updated);
                          }}
                          className="h-10"
                        />
                      </div>
                      {buildingComponents.length > 1 && (
                        <div className="col-span-1">
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              const updated = buildingComponents.filter((_, i) => i !== index);
                              setBuildingComponents(updated);
                            }}
                            className="h-10 w-10 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                          >
                            ×
                          </Button>
                        </div>
                      )}
                    </div>
                  ))}
                  
                  {/* Totals Display */}
                  <div className="flex justify-end gap-4 text-sm font-semibold pt-2">
                    <span>Total Units: {buildingComponents.reduce((sum, c) => sum + parseInt(c.units || 0), 0)}</span>
                    <span>Total Sq Ft: {buildingComponents.reduce((sum, c) => sum + parseFloat(c.sqft || 0), 0).toLocaleString()}</span>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="start-date">Start Date</Label>
                    <Input
                      id="start-date"
                      data-testid="project-start-date-input"
                      type="date"
                      value={newProject.start_date}
                      onChange={(e) => setNewProject({ ...newProject, start_date: e.target.value })}
                      className="h-12"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="completion-date">Projected Completion</Label>
                    <Input
                      id="completion-date"
                      data-testid="project-completion-date-input"
                      type="date"
                      value={newProject.projected_completion_date}
                      onChange={(e) => setNewProject({ ...newProject, projected_completion_date: e.target.value })}
                      className="h-12"
                    />
                  </div>
                </div>
                </div>
                
                {/* Submit Button - Fixed at bottom */}
                <div className="flex-shrink-0 pt-4 border-t mt-4">
                  <Button
                    type="submit"
                    data-testid="submit-project-button"
                    className="w-full h-12 bg-blue-600 hover:bg-blue-700 text-white rounded-full font-semibold"
                  >
                    Create Project
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>

        {/* Upgrade Modal */}
        <UpgradeModal
          isOpen={showUpgradeModal}
          onClose={() => setShowUpgradeModal(false)}
          currentTier={subscriptionStatus?.tier_id || 'basic'}
          currentProjects={subscriptionStatus?.current_projects || 0}
          maxProjects={subscriptionStatus?.max_projects || 2}
          onSelectPlan={handleUpgradeSelect}
        />

        {/* Select Project for Draw Request Dialog */}
        <SelectProjectForDrawDialog
          isOpen={isSelectProjectDialogOpen}
          onClose={() => setIsSelectProjectDialogOpen(false)}
          projects={projects}
        />

      {/* Edit Project Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={(open) => {
        setIsEditDialogOpen(open);
        if (!open) setEditingProject(null);
      }}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="text-xl font-bold">Edit Project</DialogTitle>
            <DialogDescription>
              Update your project details below
            </DialogDescription>
          </DialogHeader>
          {editingProject && (
            <form onSubmit={handleUpdateProject} className="flex flex-col flex-1 overflow-hidden">
              <div className="flex-1 overflow-y-auto space-y-4 pr-2">
                <div className="space-y-2">
                  <Label htmlFor="edit-project-name">Project Name</Label>
                  <Input
                    id="edit-project-name"
                    value={editingProject.name}
                    onChange={(e) => setEditingProject({ ...editingProject, name: e.target.value })}
                    placeholder="e.g., New Build 38th ST"
                    required
                    className="h-12"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-address">Address</Label>
                  <Input
                    id="edit-address"
                    value={editingProject.address}
                    onChange={(e) => setEditingProject({ ...editingProject, address: e.target.value })}
                    placeholder="Full property address"
                    required
                    className="h-12"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="edit-property-type">Property Type</Label>
                    <Select
                      value={editingProject.property_type}
                      onValueChange={(value) => setEditingProject({ ...editingProject, property_type: value })}
                    >
                      <SelectTrigger id="edit-property-type" className="h-12">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="residential">Residential</SelectItem>
                        <SelectItem value="commercial">Commercial</SelectItem>
                        <SelectItem value="mixed">Mixed Use</SelectItem>
                        <SelectItem value="multi_family">Multi-Family</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit-units">Units</Label>
                    <Input
                      id="edit-units"
                      type="number"
                      min="1"
                      value={editingProject.units}
                      onChange={(e) => setEditingProject({ ...editingProject, units: e.target.value })}
                      className="h-12"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-sqft" className="text-blue-600 font-semibold">Square Feet (Heated)</Label>
                  <Input
                    id="edit-sqft"
                    type="number"
                    min="0"
                    step="0.01"
                    value={editingProject.sqft}
                    onChange={(e) => setEditingProject({ ...editingProject, sqft: e.target.value })}
                    placeholder="Enter heated square feet only"
                    className="h-12 border-blue-300 focus:border-blue-500"
                  />
                  <p className="text-xs text-slate-500">Only count heated/finished square footage</p>
                </div>
                
                <div className="border-t pt-4">
                  <h4 className="font-semibold text-slate-900 mb-3">Budget Details</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="edit-purchase-home">Purchase Price (Home)</Label>
                      <Input
                        id="edit-purchase-home"
                        type="number"
                        min="0"
                        value={editingProject.purchase_price_home}
                        onChange={(e) => setEditingProject({ ...editingProject, purchase_price_home: e.target.value })}
                        className="h-12"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="edit-purchase-land">Purchase Price (Land)</Label>
                      <Input
                        id="edit-purchase-land"
                        type="number"
                        min="0"
                        value={editingProject.purchase_price_land}
                        onChange={(e) => setEditingProject({ ...editingProject, purchase_price_land: e.target.value })}
                        className="h-12"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="edit-construction">Construction Budget</Label>
                      <Input
                        id="edit-construction"
                        type="number"
                        min="0"
                        value={editingProject.construction_budget}
                        onChange={(e) => setEditingProject({ ...editingProject, construction_budget: e.target.value })}
                        className="h-12"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="edit-contingency">Contingency Budget</Label>
                      <Input
                        id="edit-contingency"
                        type="number"
                        min="0"
                        value={editingProject.contingency_budget}
                        onChange={(e) => setEditingProject({ ...editingProject, contingency_budget: e.target.value })}
                        className="h-12"
                      />
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="edit-start-date">Start Date</Label>
                    <Input
                      id="edit-start-date"
                      type="date"
                      value={editingProject.start_date}
                      onChange={(e) => setEditingProject({ ...editingProject, start_date: e.target.value })}
                      className="h-12"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit-completion-date">Projected Completion</Label>
                    <Input
                      id="edit-completion-date"
                      type="date"
                      value={editingProject.projected_completion_date}
                      onChange={(e) => setEditingProject({ ...editingProject, projected_completion_date: e.target.value })}
                      className="h-12"
                    />
                  </div>
                </div>

                {/* Loan Term & Listing Details (Feb 2026) */}
                <div className="border-t pt-4">
                  <h4 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                    <DollarSign className="w-4 h-4 text-blue-600" />Loan Term &amp; Listing Details
                  </h4>
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="edit-loan-term">Loan Term (months)</Label>
                        <div className="flex gap-2">
                          <Select
                            value={[3, 6, 9, 12, 24].includes(parseInt(editingProject.loan_period_months || 0)) 
                              ? String(editingProject.loan_period_months) 
                              : (editingProject.loan_period_months ? 'custom' : '')}
                            onValueChange={(value) => {
                              if (value === 'custom') {
                                setEditingProject({ ...editingProject, loan_period_months: editingProject.loan_period_months || '' });
                              } else if (value === '') {
                                setEditingProject({ ...editingProject, loan_period_months: '' });
                              } else {
                                setEditingProject({ ...editingProject, loan_period_months: value });
                              }
                            }}
                          >
                            <SelectTrigger className="h-12" data-testid="edit-loan-term-select">
                              <SelectValue placeholder="Select term" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="3">3 months</SelectItem>
                              <SelectItem value="6">6 months</SelectItem>
                              <SelectItem value="9">9 months</SelectItem>
                              <SelectItem value="12">12 months</SelectItem>
                              <SelectItem value="24">24 months</SelectItem>
                              <SelectItem value="custom">Custom...</SelectItem>
                            </SelectContent>
                          </Select>
                          {(![3, 6, 9, 12, 24].includes(parseInt(editingProject.loan_period_months || 0)) && editingProject.loan_period_months) && (
                            <Input
                              type="number"
                              min="1"
                              placeholder="months"
                              value={editingProject.loan_period_months || ''}
                              onChange={(e) => setEditingProject({ ...editingProject, loan_period_months: e.target.value })}
                              className="h-12 w-24"
                              data-testid="edit-loan-term-custom"
                            />
                          )}
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label className="flex items-center gap-2 mb-1">
                          <input
                            type="checkbox"
                            checked={!!editingProject.loan_term_extended}
                            onChange={(e) => setEditingProject({
                              ...editingProject,
                              loan_term_extended: e.target.checked,
                              loan_extended_to_months: e.target.checked ? editingProject.loan_extended_to_months : null,
                            })}
                            className="rounded"
                            data-testid="edit-loan-extended-checkbox"
                          />
                          <span>Loan term extended?</span>
                        </Label>
                        {editingProject.loan_term_extended && (
                          <Input
                            type="number"
                            min="1"
                            placeholder="New total term (months)"
                            value={editingProject.loan_extended_to_months || ''}
                            onChange={(e) => setEditingProject({ ...editingProject, loan_extended_to_months: e.target.value })}
                            className="h-12"
                            data-testid="edit-loan-extended-months"
                          />
                        )}
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Listing Type</Label>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="edit-listing-type-group">
                        {[
                          { value: 'brokerage', label: 'Brokerage' },
                          { value: 'owner_listed', label: 'Owner-Listed' },
                          { value: 'rental', label: 'Rental' },
                          { value: 'n_a', label: 'N/A' },
                        ].map((opt) => (
                          <button
                            key={opt.value}
                            type="button"
                            onClick={() => setEditingProject({
                              ...editingProject,
                              listing_type: opt.value,
                              brokerage_name: opt.value === 'brokerage' ? (editingProject.brokerage_name || '') : '',
                              agent_name: opt.value === 'brokerage' ? (editingProject.agent_name || '') : '',
                            })}
                            className={`px-3 py-2 rounded-lg border text-xs font-medium transition-colors ${
                              editingProject.listing_type === opt.value
                                ? 'bg-blue-600 text-white border-blue-600'
                                : 'bg-white text-slate-700 border-slate-200 hover:border-slate-300'
                            }`}
                            data-testid={`edit-listing-type-${opt.value}`}
                          >
                            {opt.label}
                          </button>
                        ))}
                      </div>
                    </div>
                    {editingProject.listing_type === 'brokerage' && (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="edit-brokerage-name">Brokerage Name</Label>
                          <Input
                            id="edit-brokerage-name"
                            list="brokerage-suggestions"
                            value={editingProject.brokerage_name || ''}
                            onChange={(e) => setEditingProject({ ...editingProject, brokerage_name: e.target.value })}
                            placeholder="e.g., Skogman Realty"
                            className="h-12"
                            data-testid="edit-brokerage-name"
                          />
                          <datalist id="brokerage-suggestions">
                            {[
                              'Keller Williams Realty', 'RE/MAX', 'Coldwell Banker', 'Century 21', 'Compass',
                              'Berkshire Hathaway HomeServices', 'eXp Realty', 'Sotheby\u2019s International Realty',
                              'Howard Hanna Real Estate Services', 'Better Homes and Gardens Real Estate',
                              'Weichert Realtors', 'Realty ONE Group', 'Redfin', 'Douglas Elliman', 'United Real Estate',
                              'Engel & Volkers', 'Long & Foster', 'HomeSmart', 'Corcoran Group', 'JPAR Real Estate',
                              'NextHome', 'ERA Real Estate', 'Crye-Leike', 'Skogman Realty', 'Pearson Realty',
                              'Iowa Realty', 'Lepic-Kroeger Realtors',
                            ].map((b) => <option key={b} value={b} />)}
                          </datalist>
                          <p className="text-xs text-slate-500">Start typing to see suggestions from major US brokerages</p>
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="edit-agent-name">Listing Agent Name</Label>
                          <Input
                            id="edit-agent-name"
                            value={editingProject.agent_name || ''}
                            onChange={(e) => setEditingProject({ ...editingProject, agent_name: e.target.value })}
                            placeholder="Agent's full name"
                            className="h-12"
                            data-testid="edit-agent-name"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Lien Waiver Information */}
                <div className="border-t pt-4">
                  <h4 className="font-semibold text-slate-900 mb-3">Lien Waiver Information</h4>
                  <p className="text-xs text-slate-500 mb-3">These fields are used when generating lien waiver documents</p>
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="edit-legal-description">Legal Description</Label>
                      <Input
                        id="edit-legal-description"
                        value={editingProject.legal_description || ''}
                        onChange={(e) => setEditingProject({ ...editingProject, legal_description: e.target.value })}
                        placeholder="e.g., Lot 5, Block 2, Cedar Heights Addition"
                        className="h-12"
                      />
                      <p className="text-xs text-slate-500">Short legal description of the property</p>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="edit-return-address">Return Address (for Lien Waivers)</Label>
                      <textarea
                        id="edit-return-address"
                        value={editingProject.return_address || ''}
                        onChange={(e) => setEditingProject({ ...editingProject, return_address: e.target.value })}
                        placeholder="Enter the address where contractors should return signed lien waivers"
                        className="w-full min-h-[80px] px-3 py-2 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        rows={3}
                      />
                      <p className="text-xs text-slate-500">Multi-line address shown at the bottom of lien waiver documents</p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="flex-shrink-0 pt-4 border-t mt-4">
                <Button
                  type="submit"
                  className="w-full h-12 bg-blue-600 hover:bg-blue-700 text-white rounded-full font-semibold"
                >
                  Save Changes
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default Dashboard;
